import {
  CForm,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CFormInput,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { FormEvent, Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import PromptModal from "../../components/modals/PromptModal";
import { Field } from "../../components/ui/FormUi";
import RenderTable from "../../components/ui/RenderTable";
import { colors } from "../../config/theme";
import { Chip, pluralize } from "../../helpers/general";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import AdminRoute from "../auth/RestrictedRoute";
import { cardStyle } from "../Places/PlacesTable"; // adjust path
import { tdStyle, thStyle } from "../../helpers/constants";

const isLink = (v?: string) =>
  !!v && (v.startsWith("https") || v.startsWith("http") || v.startsWith("www"));

const toHref = (v: string) => (v.startsWith("www") ? `https://${v}` : v);

export default function Variables() {
  const [showModal, setShowModal] = useState(false);

  const { data, isLoading } = useQuery<Variable[]>(["getAllVariables"], () =>
    apiService.MakeGetRequest("variables"),
  );

  return (
    <AdminRoute>
      <section>
        {/* Title + count + add */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fs-4 fw-bold mb-0">Variables</h5>
            {data && (
              <Chip tone="warning">
                {data.length} {pluralize("Variable", data.length)}
              </Chip>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="btn btn-primary text-white rounded-pill px-3"
            style={{ height: 38, fontSize: 13 }}
          >
            + Ajouter une variable
          </button>
        </div>

        {/* Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <RenderTable
            loading={isLoading}
            render={() =>
              data?.length ? (
                <CTable responsive hover align="middle" className="mb-0">
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell scope="col" style={thStyle}>
                        Nom
                      </CTableHeaderCell>
                      <CTableHeaderCell scope="col" style={thStyle}>
                        Valeur
                      </CTableHeaderCell>
                      <CTableHeaderCell scope="col" style={thStyle} />
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {data.map((variable) => (
                      <TableRow key={variable.id} variable={variable} />
                    ))}
                  </CTableBody>
                </CTable>
              ) : (
                <p className="text-center text-muted py-5 mb-0">
                  {general.fr.noData}
                </p>
              )
            }
          />
        </div>

        <VariableModal open={showModal} onClose={() => setShowModal(false)} />
      </section>
    </AdminRoute>
  );
}

function TableRow({ variable }: { variable: Variable }) {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const queryClient = useQueryClient();

  const deleteVariable = useMutation((id: number) =>
    apiService.MakeDeleteRequest(`variables/${id}`, token),
  );

  const removeVariable = () => {
    deleteVariable.mutate(variable.id, {
      onError() {
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess(general.fr.message.variableDeleted);
        queryClient.invalidateQueries("getAllVariables");
      },
    });
  };

  return (
    <Fragment>
      <CTableRow>
        <CTableDataCell style={tdStyle}>
          <span className="fw-semibold">{variable?.label}</span>
        </CTableDataCell>
        <CTableDataCell style={tdStyle}>
          {isLink(variable?.value) ? (
            <a
              href={toHref(variable.value)}
              target="_blank"
              rel="noreferrer"
              className="text-break"
            >
              {variable.value}
            </a>
          ) : (
            <span
              className="rounded-2 px-2 py-1 text-break"
              style={{
                fontFamily: "monospace",
                fontSize: 12,
                backgroundColor: "#f5f5f5",
              }}
            >
              {variable?.value}
            </span>
          )}
        </CTableDataCell>
        <CTableDataCell style={tdStyle}>
          <div className="d-flex align-items-center justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-sm rounded-pill border px-3"
              title="Modifier"
              onClick={() => setShowModal(true)}
            >
              <EditIcon width="16" height="16" fill={colors.secondary} />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger rounded-pill px-3"
              title="Supprimer"
              disabled={deleteVariable.isLoading}
              onClick={() => setShowDeleteModal(true)}
            >
              <DeleteIcon width="16" height="16" />
            </button>
          </div>
        </CTableDataCell>
      </CTableRow>

      <VariableModal
        onClose={() => setShowModal(false)}
        editMode
        open={showModal}
        variable={variable}
      />
      <PromptModal
        open={showDeleteModal}
        onAccept={removeVariable}
        onClose={() => setShowDeleteModal(false)}
      />
    </Fragment>
  );
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  variable?: Variable;
  editMode?: boolean;
}

function VariableModal({
  onClose,
  open,
  variable,
  editMode = false,
}: ModalProps) {
  const { token } = useCookie("vToken");
  const { toastError } = useToast();
  const queryClient = useQueryClient();

  const createVariable = useMutation((data: Omit<Variable, "id">) =>
    apiService.MakePostRequest("variables", data, token),
  );
  const updateVariable = useMutation((data: Variable) =>
    apiService.MakePatchRequest(`variables/${data.id}`, data, token),
  );

  const isSaving = createVariable.isLoading || updateVariable.isLoading;

  const formSubmitHandler = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      label: formData.get("label") as string,
      value: formData.get("value") as string,
    };
    const options = {
      onSuccess() {
        queryClient.invalidateQueries("getAllVariables");
        onClose();
      },
      onError() {
        toastError(general.fr.message.operationFailed);
      },
    };

    if (editMode) {
      updateVariable.mutate({ ...data, id: variable?.id! }, options);
    } else {
      createVariable.mutate(data, options);
    }
  };

  return (
    <CModal
      alignment="center"
      visible={open}
      onClose={() => !isSaving && onClose()}
    >
      <CModalHeader
        className="px-4 py-3"
        style={{ borderBottom: "1px solid #eee" }}
      >
        <CModalTitle className="fw-bold fs-5">
          {editMode ? "Mise à jour de la variable" : "Ajouter une variable"}
        </CModalTitle>
      </CModalHeader>
      <CModalBody className="px-4 py-4">
        <CForm onSubmit={formSubmitHandler}>
          <div className="row g-3">
            <Field col="col-12" label="Label" htmlFor="label" required>
              <CFormInput
                id="label"
                name="label"
                placeholder="Label"
                required
                defaultValue={variable?.label}
              />
            </Field>
            <Field col="col-12" label="Valeur" htmlFor="value" required>
              <CFormInput
                id="value"
                name="value"
                placeholder="Valeur"
                required
                defaultValue={variable?.value}
              />
            </Field>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              type="button"
              disabled={isSaving}
              onClick={onClose}
              className="btn btn-outline-secondary rounded-pill px-4"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="btn btn-success text-white rounded-pill px-4"
            >
              {isSaving ? <CSpinner size="sm" /> : "Enregistrer"}
            </button>
          </div>
        </CForm>
      </CModalBody>
    </CModal>
  );
}
